import * as React from 'react';
import i18n from 'i18next';
import _set from 'lodash/set';
import { action, observable, runInAction } from 'mobx';
import { observer } from 'mobx-react-lite';

// components
import ActionInput from 'components/_UI/Forms/Actions/ActionInput';
import { Flexbox, IconButton } from 'components/spaceKit';
import ActionColorSwatchGroup from 'components/_UI/Forms/Actions/ActionColorSwatchGroup';
import ActionSelect from 'components/_UI/Forms/Actions/ActionSelect';
import LabeledFormElement from 'components/_UI/Forms/LabeledFormElement/LabeledFormElement';

// constants
import { BrandColorsArray } from '@sprinklr/display-builder/stores/PanelTemplatesStore/PanelTemplatesTheme/constants';

// utils
import { updateWidgetOptions } from 'utils/Widget/WidgetUtils';
import { ColorTag } from '@sprinklr/display-builder/widgets/utils/getColorTag';
import { TimeDurationTunable } from '../timeDurationTunable';
import { getIsDuration } from './utils/getIsDuration';
import { getMetricDesignOptionsKey } from '../metricDesignTunables/utils/getMetricDesignOptionsKey';

// styled
import { StyledInput } from './styledComponents';

//types
import { ColorTagTunablesProps } from './types';

// constants
import { COMPARISON_OPTIONS } from './constants';

function ColorTagTunables({ widget, projection, rootOption = false }: ColorTagTunablesProps) {
    const isDuration = getIsDuration(projection);
    const metricKey = getMetricDesignOptionsKey(
        projection?.measurementName,
        projection?.aggregateFunction
    );
    const options = rootOption
        ? widget?.options
        : widget?.options?.metricDesignOptions?.[metricKey];
    const { colorTags }: { colorTags: ColorTag[] } = options || ({} as { colorTags: ColorTag[] });

    const removeTag = index => {
        runInAction(() => colorTags.splice(index, 1));
    };

    const resolveDuration = (value: number | Duration): Duration => {
        return typeof value === 'object' ? value : undefined;
    };
    const addTag = action(() => {
        const duration: Duration = resolveDuration(lastTag?.value);
        const value: number = !duration && (lastTag?.value as number);

        const newTag = observable({
            value:
                lastTag?.value &&
                (duration ? duration : value + (lastTag?.comparison === '>' ? -1 : 1)),
            comparison: lastTag?.comparison || '>',
            color:
                BrandColorsArray.slice()
                    .reverse()
                    .find(color => !colorTags?.find(colorTag => colorTag.color === color)) ||
                lastTag?.color,
        });
        if (colorTags) {
            colorTags.push(newTag);
        } else {
            const newColorTags = observable([newTag]);
            if (rootOption) {
                updateWidgetOptions(widget, `colorTags`, newColorTags);
            }
            updateWidgetOptions(widget, `metricDesignOptions.${metricKey}.colorTags`, newColorTags);
        }
    });
    const handleUpdateTimeDuration = action((duration, index) => {
        colorTags[index].value = duration;
    });
    const lastTag = colorTags?.[colorTags?.length - 1];

    return (
        <>
            <LabeledFormElement label={i18n.t('Color Tag')} gap='l'>
                {colorTags?.map((tag, index) => (
                    <Flexbox key={index} middle gap='ml'>
                        <ActionColorSwatchGroup value='color' option={tag} />
                        <StyledInput>
                            <ActionSelect
                                option={tag}
                                value='comparison'
                                options={COMPARISON_OPTIONS}
                            />
                        </StyledInput>

                        {isDuration ? (
                            <Flexbox>
                                <TimeDurationTunable
                                    tag={tag}
                                    onFormSubmit={duration =>
                                        handleUpdateTimeDuration(duration, index)
                                    }
                                />
                            </Flexbox>
                        ) : (
                            <ActionInput
                                numeric
                                type='number'
                                placeholder={i18n.t('Value')}
                                option={tag}
                                value='value'
                            />
                        )}

                        <IconButton
                            icon='icon-minus'
                            onClick={() => removeTag(index)}
                            data-testid='icon-button-remove-color-tag'
                        />
                        {colorTags.length - 1 === index ? (
                            <IconButton
                                icon='icon-plus'
                                onClick={() => addTag()}
                                data-testid='icon-button-add-color-tag'
                            />
                        ) : null}
                    </Flexbox>
                ))}
                {!colorTags?.length ? (
                    <IconButton
                        icon='icon-plus'
                        onClick={() => addTag()}
                        data-testid='icon-button-add-color-tag'
                    />
                ) : null}
            </LabeledFormElement>
        </>
    );
}

export default observer(ColorTagTunables);
